import React, { useState, useEffect, useContext } from "react";
import { Card, CardBody } from "reactstrap";
import { Link } from "react-router-dom";
import { request } from "../../utils/request";
import { ColorModeContext } from "../../index";
import "./dashboard.scss";

/**
 * Minimal visual polish (keeps all sizes identical):
 * - Injected tiny CSS for glassy gradients, icon circle, live badge, hover lift
 * - Badge is absolutely positioned so it doesn't affect layout sizing
 * - No changes to data-fetch logic or tile minima/padding — only visual improvements
 */

const injectMiniWidgetStyles = () => {
  if (document.getElementById("mini-widget-polish")) return;
  const css = `
    /* Container theme classes */
    .mw-light-root { --bg: #fff; --tile-bg: #f9fafc; --muted: #666; --text: #0b1220; --tile-border: rgba(6,15,34,0.04); --icon-ghost: rgba(3,7,18,0.03); --shadow: 0 1px 2px rgba(0,0,0,0.06); }
    .mw-dark-root { --bg: #252b3b; --tile-bg: #2f3649; --muted: #c9c9c9; --text: #f5f5f5; --tile-border: rgba(255,255,255,0.05); --icon-ghost: rgba(255,255,255,0.03); --shadow: 0 1px 3px rgba(0,0,0,0.35); }

    /* Tile base */
    .mini-tile {
      position: relative;
      overflow: hidden;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      transition: transform 160ms ease, box-shadow 160ms ease;
      /* keep layout sizing identical to inline styles in component */
    }

    /* Glass + subtle inner sheen */
    .mini-tile::after {
      content: "";
      position: absolute;
      inset: 0;
      pointer-events: none;
      background: linear-gradient(180deg, rgba(255,255,255,0.02), rgba(0,0,0,0.02));
      mix-blend-mode: overlay;
    }

    /* Hover lift */
    .mini-tile:hover { transform: translateY(-4px); box-shadow: 0 8px 20px rgba(11,20,37,0.08); }

    /* Left content */
    .mini-left { z-index: 1; flex: 1; overflow: hidden; padding-right: 8px; min-width: 0; }
    .mini-title { margin: 0; font-size: 0.88rem; color: var(--text); font-weight: 600; white-space: nowrap; text-overflow: ellipsis; overflow: hidden; }
    .mini-value { margin: 4px 0 0; font-size: 1rem; color: var(--text); font-weight: 600; }
    .mini-desc { display: block; margin-top: 6px; color: var(--muted); font-size: 0.72rem; }

    /* Right content */
    .mini-right { z-index: 1; display:flex; flex-direction: column; align-items: flex-end; gap: 6px; min-width: 44px; margin-left: 0.4rem; }
    .mini-icon-circle {
      width: 36px;
      height: 36px;
      border-radius: 8px;
      display:flex;
      align-items:center;
      justify-content:center;
      box-shadow: inset 0 -6px 14px rgba(0,0,0,0.04);
      background: var(--icon-ghost);
      color: var(--text);
      flex-shrink: 0;
    }
    .mini-icon-circle i { display:inline-block; line-height:1; }

    /* tiny live badge (absolute so it does not affect size) */
    .mini-live-badge {
      position: absolute;
      right: 8px;
      top: 8px;
      z-index: 2;
      font-size: 10px;
      padding: 4px 8px;
      border-radius: 999px;
      font-weight: 700;
      background: linear-gradient(90deg, rgba(0,191,99,0.12), rgba(0,191,99,0.04));
      color: #036b27;
      box-shadow: 0 2px 6px rgba(3, 107, 39, 0.06);
      pointer-events: none;
    }

    /* ensure icons don't visually expand tile (keeps size) */
    .mini-icon-circle svg, .mini-icon-circle i { width: 16px; height: 16px; }

    /* Subtle border for light/dark */
    .mw-light-root .mini-tile { border: 1px solid var(--tile-border); background: linear-gradient(180deg, rgba(255,255,255,0.6), rgba(243,247,255,0.7)); box-shadow: var(--shadow); }
    .mw-dark-root .mini-tile { border: 1px solid var(--tile-border); background: linear-gradient(180deg, rgba(255,255,255,0.02), rgba(0,0,0,0.02)); box-shadow: var(--shadow); }

    /* small responsiveness - keep everything compact */
    @media (max-width: 720px) {
      .mini-title { white-space: normal; }
    }
  `;
  const s = document.createElement("style");
  s.id = "mini-widget-polish";
  s.appendChild(document.createTextNode(css));
  document.head.appendChild(s);
};

const MiniWidgets = () => {
  const { mode } = useContext(ColorModeContext);

  // apply injected styles once
  useEffect(() => {
    injectMiniWidgetStyles();
  }, []);

  const [groupCount, setGroupCount] = useState(0);
  const [policiesCount, setPoliciesCount] = useState(0);
  const [whitelistCount, setWhitelistCount] = useState(0);
  const [activeSessionsCount, setActiveSessionsCount] = useState(0);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const grpData = await request("groups", { method: "GET" });
        setGroupCount(Array.isArray(grpData) ? grpData.length : 0);

        const polData = await request("policies", { method: "GET" });
        setPoliciesCount(Array.isArray(polData) ? polData.length : 0);

        const urlData = await request("urls", { method: "GET" });
        setWhitelistCount(Array.isArray(urlData) ? urlData.length : 0);
      } catch (error) {
        console.error("Error fetching counts (groups/policies/urls):", error);
      }
    };

    const fetchActiveSessions = async () => {
      try {
        const resp = await request("sessions", { method: "GET" });
        let sessions = [];
        if (Array.isArray(resp)) sessions = resp;
        else if (resp && Array.isArray(resp.data)) sessions = resp.data;
        else if (resp && Array.isArray(resp.rows)) sessions = resp.rows;
        else if (resp?.data?.docs) sessions = resp.data.docs;

        const active = sessions.filter((s) => {
          const exp = s?.expiresAt;
          return exp === null || exp === undefined || exp === "" || String(exp).toLowerCase() === "null";
        }).length;

        setActiveSessionsCount(active);
      } catch (err) {
        console.error("Error fetching sessions:", err);
        setActiveSessionsCount(0);
      }
    };

    fetchCounts();
    fetchActiveSessions();

    const interval = setInterval(fetchActiveSessions, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const reports = [
    {
      title: "Groups",
      value: groupCount,
      icon: "mdi mdi-account-group",
      desc: ` configured`,
      link: "/groups",
    },
    {
      title: "Policies",
      value: policiesCount,
      icon: "mdi mdi-shield-outline",
      desc: ` configured`,
      link: "/policies",
    },
    {
      title: "Whitelisted URLs",
      value: whitelistCount,
      icon: "mdi mdi-link",
      desc: ` domains allowed`,
      link: "/urls",
    },
    {
      title: "Active Sessions",
      value: activeSessionsCount,
      icon: "mdi mdi-monitor",
      desc: ` sessions active`,
      link: "/active-sessions",
    },
  ];

  // same sizing as before (keeps everything compact)
  const tileMin = 150; // px
  const tilePadding = "0.45rem 0.6rem";
  const titleFontSize = "0.88rem";
  const valueFontSize = "1rem";
  const iconSize = 16;
  const hoverScale = 1.01;

  // root theme class to let CSS vars choose light/dark styles
  const rootClass = mode === "dark" ? "mw-dark-root" : "mw-light-root";

  return (
    <div className={`widget-card ${rootClass}`} style={{ width: "100%" }}>
      <Card
        style={{
          height: "100%",
          backgroundColor: mode === "dark" ? "#252b3b" : "#fff",
          border: mode === "dark" ? "none" : "1px solid #e0e0e0",
          borderRadius: "12px",
        }}
      >
        <CardBody
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            padding: "0.9rem",
            height: "100%",
            boxSizing: "border-box",
          }}
        >
          <h4 style={{ marginBottom: "0.5rem", color: mode === "dark" ? "#f5f5f5" : "#000", fontSize: "1rem" }}>Overview</h4>

          <div
            style={{
              flex: "1 1 auto",
              display: "grid",
              gridTemplateColumns: `repeat(auto-fit, minmax(${tileMin}px, 1fr))`,
              gap: "0.5rem",
              minHeight: 0,
            }}
          >
            {reports.map((report, idx) => (
              <Link
                to={report.link}
                key={idx}
                style={{ textDecoration: "none", color: mode === "dark" ? "#f5f5f5" : "inherit", display: "block", height: "100%" }}
              >
                <div
                  className="mini-tile"
                  style={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: tilePadding,
                    backgroundColor: mode === "dark" ? "#2f3649" : "#f9fafc",
                    borderRadius: 10,
                    border: mode === "dark" ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e6e9ef",
                    boxShadow: mode === "dark" ? "0 1px 3px rgba(0,0,0,0.35)" : "0 1px 2px rgba(0,0,0,0.06)",
                    transition: "transform 160ms ease, box-shadow 160ms ease",
                    minHeight: 56,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.transform = `scale(${hoverScale})`)}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                >
                  

                  <div className="mini-left">
                    <p className="text-truncate font-size-14 mb-1" style={{ margin: 0 }}>
                      <span style={{ color: mode === "dark" ? "#f5f5f5" : "#000", fontSize: titleFontSize }}>{report.title}</span>
                    </p>
                    <h5
                      style={{
                        margin: 0,
                        marginTop: 4,
                        fontSize: valueFontSize,
                        color: mode === "dark" ? "#f5f5f5" : "#000",
                        fontWeight: 600,
                      }}
                      className="mini-value"
                    >
                      {report.value}
                    </h5>
                    <small className="mini-desc" style={{ color: mode === "dark" ? "#c9c9c9" : "#666", fontSize: "0.72rem" }}>{report.desc}</small>
                  </div>

                  <div className="mini-right">
                    <div
                      className="mini-icon-circle"
                      style={{
                        background: mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(3,7,18,0.03)",
                        color:"#2d9cdb",
                      }}
                    >
                      {/* keep using mdi classes if available; else show fallback */}
                      <i className={`${report.icon}`} style={{ fontSize: iconSize }} aria-hidden />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default MiniWidgets;
